import type React from "react"

import { useState } from "react"
import { Button } from "../components/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../components/ui/card"
import { Input } from "../components/ui/input"
import { Label } from "../components/ui/label"
import { Checkbox } from "../components/ui/checkbox"
import { Eye, EyeOff, Mail, Lock, User, UserCheck } from "lucide-react"
import { Link, useNavigate } from "react-router-dom"
import { signUp } from "../api/axios"

interface SignUpFormData {
  username: string
  password: string
  displayname: string
  email: string
}


export default function SignUpPage() {
  const navigate = useNavigate()
  const [formData, setFormData] = useState<SignUpFormData>({
    username: "",
    password: "",
    displayname: "",
    email: "",
  })
  const [confirmPassword, setConfirmPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [acceptTerms, setAcceptTerms] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<Partial<SignUpFormData & { confirmPassword: string; terms: string; api: string; org: string }>>({})
  const [orgMode, setOrgMode] = useState<'create' | 'join' | ''>('');
  const [orgName, setOrgName] = useState('');
  const [joinCode, setJoinCode] = useState('');

  const handleInputChange = (field: keyof SignUpFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Partial<SignUpFormData & { confirmPassword: string; terms: string; api: string; org: string }> = {}

    if (!formData.username.trim()) {
      newErrors.username = "Username is required"
    } else if (formData.username.length < 3) {
      newErrors.username = "Username must be at least 3 characters"
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required"
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address"
    }

    if (!formData.displayname.trim()) {
      newErrors.displayname = "Display name is required"
    }

    if (!formData.password) {
      newErrors.password = "Password is required"
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters"
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password"
    } else if (formData.password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match"
    }

    if (!acceptTerms) {
      newErrors.terms = "You must accept the terms and conditions"
    }

    if (!orgMode) {
      newErrors.org = 'Please select organization option';
    } else if (orgMode === 'create' && !orgName.trim()) {
      newErrors.org = 'Organization name is required';
    } else if (orgMode === 'join' && !joinCode.trim()) {
      newErrors.org = 'Joining code is required';
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsLoading(true)
    setErrors({})

    try {
      await signUp({
        username: formData.username,
        password: formData.password,
        displayname: formData.displayname,
        email: formData.email,
        organizationType: orgMode,
        organizationName: orgMode === 'create' ? orgName : undefined,
        joinCode: orgMode === 'join' ? joinCode : undefined,
        active: true,
      });
      navigate("/login")
    } catch (error: any) {
      setErrors({ api: error.message || error.response?.data?.message || "An unexpected error occurred." })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">Create Account</CardTitle>
          <CardDescription className="text-center">Enter your information to create a new account</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {errors.api && <p className="text-sm text-red-600 text-center">{errors.api}</p>}
            <div className="space-y-2">
              <Label htmlFor="orgMode">Organization</Label>
              <select
                id="orgMode"
                value={orgMode}
                onChange={e => {
                  setOrgMode(e.target.value as 'create' | 'join' | '');
                  setErrors(prev => ({ ...prev, org: '' }));
                }}
                className="w-full border rounded px-3 py-2"
                required
              >
                <option value="">Select option</option>
                <option value="create">Create Organization</option>
                <option value="join">Join Organization</option>
              </select>
              {orgMode === 'create' ? (
                <Input
                  id="orgName"
                  type="text"
                  placeholder="Organization Name"
                  value={orgName}
                  onChange={e => setOrgName(e.target.value)}
                  className="mt-2"
                  required
                />
              ) : null}
              {orgMode === 'join' ? (
                <Input
                  id="joinCode"
                  type="text"
                  placeholder="Joining Code"
                  value={joinCode}
                  onChange={e => setJoinCode(e.target.value)}
                  className="mt-2"
                  required
                />
              ) : null}
              {errors.org && <p className="text-sm text-red-600">{errors.org}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <div className="relative">
                <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="username"
                  type="text"
                  placeholder="Enter your username"
                  value={formData.username}
                  onChange={(e) => handleInputChange("username", e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
              {errors.username && <p className="text-sm text-red-600">{errors.username}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
              {errors.email && <p className="text-sm text-red-600">{errors.email}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="displayname">Display Name</Label>
              <div className="relative">
                <UserCheck className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="displayname"
                  type="text"
                  placeholder="Enter your display name"
                  value={formData.displayname}
                  onChange={(e) => handleInputChange("displayname", e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
              {errors.displayname && <p className="text-sm text-red-600">{errors.displayname}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) => handleInputChange("password", e.target.value)}
                  className="pl-10 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && <p className="text-sm text-red-600">{errors.password}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value)
                    if (errors.confirmPassword) {
                      setErrors((prev) => ({ ...prev, confirmPassword: "" }))
                    }
                  }}
                  className="pl-10 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.confirmPassword && <p className="text-sm text-red-600">{errors.confirmPassword}</p>}
            </div>

            <div className="flex items-start space-x-2">
              <Checkbox
                id="terms"
                checked={acceptTerms}
                onCheckedChange={(checked) => {
                  setAcceptTerms(checked as boolean)
                  if (errors.terms) {
                    setErrors((prev) => ({ ...prev, terms: "" }))
                  }
                }}
                className="mt-1 "
              />
              <Label htmlFor="terms" className="text-sm font-normal leading-5">
                I agree to the{" "}
                <Link to="/terms" className="text-blue-600 hover:text-blue-500 hover:underline">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link to="/privacy" className="text-blue-600 hover:text-blue-500 hover:underline">
                  Privacy Policy
                </Link>
              </Label>
            </div>
            {errors.terms && <p className="text-sm text-red-600">{errors.terms}</p>}
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button type="submit" className="w-full bg-blue-500 hover:bg-blue-600 text-black" disabled={isLoading}>
              {isLoading ? "Creating Account..." : "Create Account"}
            </Button>

            <p className="text-center text-sm text-gray-600">
              Already have an account?{" "}
              <Link to="/login" className="text-blue-600 hover:text-blue-500 hover:underline font-medium">
                Sign in
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  )
}
